import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useProfileConnectedHealthSystems from '../../../../../hooks/api/useProfileConnectedHealthSystems';
import { oneUpEndpointAddressToString } from '../../../../../utils/addressToString';
import { useBuildOneUpHealthConnectUrl } from '../../../../../hooks/useBuildOneUpHealthConnectUrl';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import useOneUpHealthConnect from '../../../../../hooks/api/useOneUpHealthConnect';
import { openURL } from 'expo-linking';
import { useAvatar } from '../../../../../hooks/useAvatar';
import { Platform } from 'react-native';
import urls from '../../../../../utils/urls';
import { getIntl } from '../../../../../utils/intl';
import LabelValueDisplay, { LabelValueItem } from '../../../../v2BaseComponents/LabelValueDisplay';
import getName from '../../../../../utils/getName';
import getDateString from '../../../../../utils/getDateString';
import SkeletonLoadingContainer from '../../../../v2BaseComponents/SkeletonLoadingContainer';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../utils/sharedMessages';
import { usePromptButton } from '../../../../shared/PromptButton';
import DeleteConfirmationPromptContents from './DeleteConfirmationPromptContents';
import PatientMismatchAlert from './PatientMismatchAlert';
import useOneUpHealthRefreshProgress from '../../../../../hooks/api/useMedicalProfileRefreshProgress';
import Alert from '../../../../v2BaseComponents/Alert';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import HelpPrompt from '../../../../shared/HelpPrompt';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import ScaledImage from '../../../../v2BaseComponents/ScaledImage';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';
import { View } from 'react-native';
import MedicalProfileUsageIndicator from '../../../../shared/MedicalProfileUsageIndicator';
import DisableAutomaticRefreshToggle from './DisableAutomaticRefreshToggle';
import { useIsProfilePaid } from '../../../../../hooks/api/useIsProfilePaid';

const intl = getIntl('portal-connection-details', {
    connectionDetails: 'Connection Details',
    disconnectFromPortal: 'Disconnect from Portal?',
    automaticRefreshLapsedHeader: 'This portal Is not automatically refreshing',
    automaticRefreshLapsedContent: 'Automatic refresh expired on {date}.  Please reconnect to refresh data.',
    automaticRefreshActive: 'This portal will automatically refresh',
    automaticRefreshActiveContent:
        'Automatically refreshing through {date}.  You should receive new data once it is available from the source system.',
    refreshDisabledHeader: 'Patient portal refresh no longer supported',
    refreshDisabledMessage:
        'Refreshing data from this patient portal has been discontinued. Reconnect via the new portal flow to keep your data up to date.',
    logoAlt: 'Health System Logo',
    refresh: 'Refresh Portals',
    disconnectPortal: 'Delete',
    portalProfile: 'Portal Profile',
    dateOfBirth: 'Date of Birth',
    connectedBy: 'Connected By',
    lastRefreshed: 'Last Refreshed',
    history: 'History',
    disabledHealthSystem: 'This connection is disabled',
    disabledHealthSystemMessage:
        'Health system connections are sometimes disabled due to issues with the connection or changes by the source health system.  This means that the system is not currently able to be refreshed.  Your data is still available, but it may not be up to date.  You can try searching and establishing a new connection to this health system.',
    noHistory: 'No history found yet.  It may take up to 3 hours for your data to be available.',
    noDataHeader: 'No Data Available Yet',
    noDataBody:
        'Data has not yet been loaded for health system.  Loading data may take up to 3 hours.  This may also occur if an error occurred connecting to your portal.  If an error did occur, please trying refreshing the connection.\n\nIf you connected to this portal by mistake, you may delete the connection below.',
    helpTitle: 'Connection History',
    helpContent: `The counts of records updated with each refresh of a portal.

While most health care systems respond with the full data in under a minute, some systems can take more than an hour.  This means that if you refresh the portal later in the same day, you may get a partial set of data.`,
});

export type PortalConnectionScreenOwnProps = {};

export type PortalConnectionScreenProps = PortalConnectionScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'portal-connection'>;
};

const HeaderImage: React.FC<{ url: string }> = ({ url }) => {
    const headerImageSize = 60;
    const image = useAvatar({
        avatarAPIUrl: Platform.select({
            // On the web, we hit a CORS issue, so we have to go through a proxy
            web: urls.api.ehrIntegrations.oneUpHealth.endpoints.image(url),
            default: url,
        }),
    });

    return (
        <ScaledImage
            uri={image?.avatar?.base64 || null}
            restrict="height"
            loaderRatio={1}
            containerStyle={{ width: headerImageSize, height: headerImageSize, borderRadius: 4 }}
            alt={intl('logoAlt')}
        />
    );
};

const PortalConnectionScreenScreen: React.FC<PortalConnectionScreenProps> = (props) => {
    const isProfilePaidHookResponse = useIsProfilePaid({ profileId: props.route.params.profileId });
    const { healthSystemId, profileId } = props.route.params;
    const recordEvent = useRecordEvent();
    const { navigate } = useProfilesTabNavigation();
    const {
        isLoading,
        connectedHealthSystems,
        pendingDataHealthSystems,
        isDisconnectingHealthSystem,
        disconnectHealthSystem,
        isLoadingHealthSystemConnectionDetails,
        healthSystemConnectionDetails,
        isLoadingHealthSystemIngestionHistory,
        healthSystemIngestionHistory,
    } = useProfileConnectedHealthSystems({
        medicalProfileId: props.route.params.profileId,
        onHealthSystemDisconnect: () => {
            navigate('connect', { profileId: props.route.params.profileId });
        },
        healthSystemId: healthSystemId,
    });
    const { isRefreshing } = useOneUpHealthRefreshProgress({
        medicalProfileId: profileId,
    });
    const deleteButtonPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <DeleteConfirmationPromptContents
                medicalProfileId={profileId}
                healthSystemId={healthSystemId}
                onClose={onClose}
                onDisconnect={() => {
                    recordEvent('deletePatientPortal');
                    disconnectHealthSystem(String(healthSystemId));
                }}
            />
        ),
        promptTitle: intl('disconnectFromPortal'),
    });
    const targetConnectedHealthSystem = [...(connectedHealthSystems || []), ...(pendingDataHealthSystems || [])].find(
        (_) => _.endpoint.id === Number(healthSystemId),
    );
    const { buildOneUpHealthConnectUrl } = useBuildOneUpHealthConnectUrl();
    const hasPortalEditPermission = useHasPermission(profileId, 'patientPortals', 'edit');
    const { setPreRedirectArgs } = usePreRedirectArgs();
    // `connect` and `initiateAutomaticRefresh` were used by the refresh button,
    // which has been disabled now that 1upHealth refresh is no longer supported.
    // We still subscribe to `isConnecting` / `isInitiatingAutomaticRefresh` so
    // the loading state stays accurate if either is ever invoked by another path.
    const { isConnecting, isInitiatingAutomaticRefresh } = useOneUpHealthConnect({
        medicalProfileId: profileId,
        onConnectUrlReceived: (url) => {
            setPreRedirectArgs(
                {
                    type: 'portal-connect',
                    profileId: props.route.params.profileId,
                    endpointId: Number(healthSystemId),
                },
                () => {
                    openURL(buildOneUpHealthConnectUrl(url));
                },
            );
        },
    });

    const labelValueItems = useMemo<LabelValueItem[]>(() => {
        return [
            {
                label: intl('portalProfile'),
                value: healthSystemConnectionDetails?.name
                    ? getName({
                          firstName: healthSystemConnectionDetails.name.first,
                          middleName: healthSystemConnectionDetails.name.middle,
                          lastName: healthSystemConnectionDetails.name.last,
                      })
                    : '',
            },
            {
                label: intl('dateOfBirth'),
                value: healthSystemConnectionDetails?.dob
                    ? getDateString(healthSystemConnectionDetails.dob, 'date')
                    : '',
            },
            {
                label: intl('connectedBy'),
                value: healthSystemConnectionDetails?.connectedBy
                    ? getName(healthSystemConnectionDetails.connectedBy)
                    : '',
            },
            {
                label: intl('lastRefreshed'),
                value: healthSystemConnectionDetails?.refreshedTime
                    ? getDateString(healthSystemConnectionDetails.refreshedTime, 'date-time')
                    : '',
            },
        ];
    }, [healthSystemConnectionDetails]);

    const isPendingConnection = pendingDataHealthSystems.some((_) => _.endpoint.id === Number(healthSystemId));

    const getHistoryLabeledValue = (
        item: APITypes.ehrIntegrationsService.OneUpDataPullHistoryEntry | null,
    ): LabelValueItem => {
        const getResourceCountsText = () => {
            if (!item)
                return new Array(4)
                    .fill(null)
                    .map(() => 'Loading...')
                    .join(', ');

            const categories: MedicalDataTypes.PrimaryRecordResourceCategory[] = [
                'allergy',
                'condition',
                'medication',
                'visit',
                'lab',
                'imaging',
                'vaccination',
                'procedure',
            ];

            const counts = categories
                .map((category) => {
                    const count = item?.resourceCounts[category] || 0;
                    const categoryLabel =
                        count === 1 ? resourceCategoryMessages(category) : pluralResourceCategoryMessages(category);
                    return count > 0 ? `${count} ${categoryLabel}` : null;
                })
                .filter(Boolean);
            return counts.join(', ');
        };

        return {
            label: item?.dateTime ? getDateString(item.dateTime, 'date-time') : '',
            value: getResourceCountsText(),
            isLoading: !item,
        };
    };

    const placeholderHistoryLabeledValue = (
        <LabelValueDisplay
            omitEmptyValues={false}
            items={[getHistoryLabeledValue(null), getHistoryLabeledValue(null), getHistoryLabeledValue(null)]}
        />
    );

    // `isEligibleForAutomaticRefresh` is still consumed by `DisableAutomaticRefreshToggle`
    // below. The downstream `isAutomaticRefreshExpired` / `canInitiateAutomaticRefresh`
    // calculations were removed because they only fed the refresh button (now disabled
    // — see below — since 1upHealth refresh is no longer supported).
    const isEligibleForAutomaticRefresh =
        !!healthSystemConnectionDetails?.automaticRefreshExpirationDateTime &&
        !targetConnectedHealthSystem?.endpoint.isDisabled &&
        !isPendingConnection;

    // 1upHealth patient portal refresh has been discontinued. Always show
    // an informational alert in place of the legacy automatic-refresh status,
    // which is no longer accurate (the refresh cron has been disabled).
    const renderRefreshAlert = () => {
        return <Alert type="info" title={intl('refreshDisabledHeader')} message={intl('refreshDisabledMessage')} />;
    };

    return (
        <>
            <AnimateLoadedContent
                isLoading={
                    !targetConnectedHealthSystem?.endpoint ||
                    isLoading ||
                    isConnecting ||
                    isInitiatingAutomaticRefresh ||
                    isProfilePaidHookResponse.isLoading
                }
            >
                {() =>
                    targetConnectedHealthSystem?.endpoint ? (
                        <>
                            <HeaderContainer>
                                <Stack vertical justify={'center'} align={'center'} space={3}>
                                    {targetConnectedHealthSystem?.endpoint.logo ? (
                                        <HeaderImage url={targetConnectedHealthSystem.endpoint.logo} />
                                    ) : null}
                                    <Stack vertical justify="center" align="center">
                                        <View>
                                            <Text variant="h4" alignText="center">
                                                {targetConnectedHealthSystem.endpoint.name}
                                            </Text>
                                        </View>
                                        <View>
                                            <Text variant="body-1" alignText="center">
                                                {oneUpEndpointAddressToString(targetConnectedHealthSystem.endpoint)}
                                            </Text>
                                        </View>
                                    </Stack>
                                </Stack>
                            </HeaderContainer>
                            <ActionButtonLayout
                                whiteBackground
                                scrollable
                                actions={[
                                    {
                                        // 1upHealth refresh is discontinued. Button kept visible
                                        // (always disabled) so users see why it's no longer actionable;
                                        // accompanying info alert above explains the change.
                                        icon: 'RefreshCw',
                                        title: intl('refresh'),
                                        onPress: () => {},
                                        isDisabled: true,
                                    },
                                    {
                                        icon: 'Trash2',
                                        title: intl('disconnectPortal'),
                                        onPress: deleteButtonPrompt.onPress,
                                        isDisabled:
                                            !hasPortalEditPermission || isDisconnectingHealthSystem || isRefreshing,
                                        show: hasPortalEditPermission,
                                        displayInMoreMenu: !isPendingConnection,
                                    },
                                ]}
                            >
                                <MainContentContainer>
                                    <ContentStack>
                                        {renderRefreshAlert()}
                                        {isEligibleForAutomaticRefresh && isProfilePaidHookResponse.isPaid ? (
                                            <DisableAutomaticRefreshToggle
                                                isEligibleForAutomaticRefresh={isEligibleForAutomaticRefresh}
                                                medicalProfileId={props.route.params.profileId}
                                                healthSystemId={props.route.params.healthSystemId}
                                            />
                                        ) : null}
                                        {targetConnectedHealthSystem.endpoint.isDisabled ? (
                                            <Alert
                                                type="error"
                                                title={intl('disabledHealthSystem')}
                                                message={intl('disabledHealthSystemMessage')}
                                            />
                                        ) : null}
                                        {isPendingConnection ? (
                                            <Alert
                                                type="warning"
                                                title={intl('noDataHeader')}
                                                message={intl('noDataBody')}
                                            />
                                        ) : null}
                                        <PatientMismatchAlert
                                            healthSystemId={healthSystemId}
                                            medicalProfileId={profileId}
                                        />
                                        <TitledSection title={intl('connectionDetails')}>
                                            <MainContentContainer>
                                                <SkeletonLoadingContainer
                                                    isLoading={isLoadingHealthSystemConnectionDetails}
                                                >
                                                    <LabelValueDisplay
                                                        items={labelValueItems}
                                                        omitEmptyValues={!isLoadingHealthSystemConnectionDetails}
                                                    />
                                                </SkeletonLoadingContainer>
                                            </MainContentContainer>
                                        </TitledSection>
                                        {!isPendingConnection ? (
                                            <TitledSection
                                                title={intl('history')}
                                                headerRight={
                                                    <Stack horizontal align="center" space={4}>
                                                        <MedicalProfileUsageIndicator
                                                            medicalProfileId={profileId}
                                                            metricType="portal-connect"
                                                        />
                                                        <HelpPrompt
                                                            title={intl('helpTitle')}
                                                            message={intl('helpContent')}
                                                        />
                                                    </Stack>
                                                }
                                            >
                                                <MainContentContainer>
                                                    {isLoadingHealthSystemIngestionHistory ? (
                                                        placeholderHistoryLabeledValue
                                                    ) : healthSystemIngestionHistory?.history?.length ? (
                                                        <LabelValueDisplay
                                                            items={(healthSystemIngestionHistory?.history || [])
                                                                .sort(
                                                                    (e1, e2) =>
                                                                        new Date(e2.dateTime).getTime() -
                                                                        new Date(e1.dateTime).getTime(),
                                                                )
                                                                .map((item) => getHistoryLabeledValue(item))}
                                                        />
                                                    ) : (
                                                        <Text variant="subtitle-2">{intl('noHistory')}</Text>
                                                    )}
                                                </MainContentContainer>
                                            </TitledSection>
                                        ) : null}
                                    </ContentStack>
                                </MainContentContainer>
                            </ActionButtonLayout>
                        </>
                    ) : null
                }
            </AnimateLoadedContent>
            {deleteButtonPrompt.Prompt}
        </>
    );
};

export default React.memo(PortalConnectionScreenScreen);
